// §18.1 shared picker: PartyPicker. Same shape as CategoryPicker's own "+Create new…"
// precedent, but `value`/`onChange` carry the party ID (a party is a live reference, unlike
// a category name) and a picker-created party is PT-6 "provisional" — an AI-adjacent, quick
// capture-time create, not a fully-filled-in profile; the owner finishes it later from S-20.
import { useState } from 'react';
import { useCreateParty, useParties } from '../lib/parties';
import { ghostButtonInlineClass, inputClass, selectClass } from './FormKit';

const CREATE_SENTINEL = '__create__';

export function PartyPicker({
  id,
  value,
  onChange,
}: {
  id: string;
  value: string;
  onChange: (partyId: string) => void;
}): React.JSX.Element {
  const { data: parties } = useParties(false);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState('');
  const createMutation = useCreateParty();

  const options = (parties ?? [])
    .filter((p) => p.mergedIntoId === undefined)
    .sort((a, b) => a.name.localeCompare(b.name));

  if (creating) {
    return (
      <div className="flex gap-2">
        <input
          className={`${inputClass} flex-1`}
          aria-label="New party name"
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
          }}
          autoFocus
        />
        <button
          type="button"
          className={ghostButtonInlineClass}
          disabled={createMutation.isPending}
          onClick={() => {
            const name = draft.trim();
            if (name === '') return;
            createMutation.mutate(
              { name, provisional: true },
              {
                onSuccess: (created) => {
                  onChange(created.id);
                  setCreating(false);
                  setDraft('');
                },
              },
            );
          }}
        >
          Create
        </button>
      </div>
    );
  }

  return (
    <select
      id={id}
      className={selectClass}
      value={value}
      onChange={(e) => {
        if (e.target.value === CREATE_SENTINEL) {
          setCreating(true);
          return;
        }
        onChange(e.target.value);
      }}
    >
      <option value="">No party</option>
      {options.map((p) => (
        <option key={p.id} value={p.id}>
          {p.name}
          {p.provisional ? ' (unconfirmed)' : ''}
        </option>
      ))}
      <option value={CREATE_SENTINEL}>+ Create new…</option>
    </select>
  );
}
